import React, { useState, useEffect, useRef } from "react";
import "./ViewCandidate.scss";
import { FileOpenOutlined, ThumbDownOffAltOutlined } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Modal } from "antd";
import { Space } from "antd";
import FeedBackService from "../APIServices/ViewCandidateService/FeedBackService";
import InterviewService from "../APIServices/ViewCandidateService/InterviewService";
import JobOfferService from "../APIServices/ViewCandidateService/JobOfferService";
import EmployeeContractService from "../APIServices/ViewCandidateService/EmployeeContractService";
import { DownloadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import {
  JobApplicationStatusEnum,
  JobApplicationStatusLabel,
  JobApplicationStatusText,
  JOB_STATUS_CONFIG,
} from "Constants/Enums/JobApplicantStatusEnum";
import { JobPostingStatusEnum } from "Constants/Enums/JobPostingStatusEnum";

const { confirm } = Modal;

// Brand-styled buttons shared across status action renders.
const brandBtn = (loading) => ({
  padding: "7px 18px",
  background: loading ? "#94b89f" : "#0f5a2a",
  color: "white",
  border: "none",
  borderRadius: "40px",
  fontSize: "0.8rem",
  fontWeight: 600,
  cursor: loading ? "not-allowed" : "pointer",
  fontFamily: "inherit",
  boxShadow: "0 2px 6px rgba(15,90,42,0.18)",
});

const ghostBtn = (disabled) => ({
  padding: "7px 18px",
  background: "#fff",
  color: disabled ? "#94b89f" : "#0f5a2a",
  border: `1px solid ${disabled ? "#d4e1d4" : "#2b7e3a"}`,
  borderRadius: "40px",
  fontSize: "0.8rem",
  fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: "inherit",
});

function ViewCandidateService() {
  const { id } = useParams();
  const jobPostingId = id;
  const [jobDetails, setJobDetails] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [isOverall, setIsOverall] = useState(true);
  const [selectedPdf, setSelectedPdf] = useState("");
  const [selectedCoverLetter, setSelectedCoverLetter] = useState("");
  const [emailSent, setEmailSent] = useState({});
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingEmails, setLoadingEmails] = useState({});
  const [loadingjobApplicantId, setLoadingjobApplicantId] = useState(null);
  const [currentFilter, setCurrentFilter] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);

  const [statusCounts, setStatusCounts] = useState({});
  const [employeeCount, setEmployeeCount] = useState(0);
  const [downloadedDocs, setDownloadedDocs] = useState({});

  const FILE_BASE_URL = process.env.REACT_APP_Base_URL;

  const {
    feedbacks,
    openfeedback,
    setOpenfeedback,
    showfeedbackModal,
    feedbackData,
    setFeedbackData,
    handleSubmitFeedback,
  } = FeedBackService(id);

  const {
    jobApplicantId,
    open,
    setOpen,
    email,
    firstDateTime,
    secondDateTime,
    thirdDateTime,
    setFirstDateTime,
    setSecondDateTime,
    setThirdDateTime,
    showModal,
    handleOk,
    handleCancel,
    interviewScheduled,
    loadingInterviewEmails,
  } = InterviewService(id);

  const { jobOfferScheduled, handleContractChange, loadingJobOffer } = JobOfferService(id);

  const {
    openContract,
    employeeContract,
    contractPdf,
    handleContractCancel,
    showcontractModal,
    handleContractPreview,
    employeeEmail,
    empId,
  } = EmployeeContractService();

  const prevStatusRef = useRef({});
  // First useEffect - Initial data fetch
  useEffect(() => {
    if (!id) return;

    fetchJobDetails();
    fetchEmployeesByjobPostingId(id);
  }, [id]);

  // Second useEffect - Refresh on status changes (optimized)
  useEffect(() => {
    if (!id) return;

    // Check if there's been an actual status change
    const currentStatuses = {
      ...interviewScheduled,
      ...jobOfferScheduled,
    };

    const hasStatusChange = Object.keys(currentStatuses).some(
      (key) => currentStatuses[key].status !== prevStatusRef.current[key]?.status
    );

    if (hasStatusChange) {
      fetchEmployeesByjobPostingId(id);
    }

    // Update the ref for next comparison
    prevStatusRef.current = currentStatuses;
  }, [id, interviewScheduled, jobOfferScheduled]);

const isOnboardingDisabled = (empId) => {
  const docs = downloadedDocs[empId];

  const requiredDocs = [
    "ndafile",
    "ncafile",
    "employeeAgreement",
    "i_nine",
    "w_four",
    "offerLetter",
    "hrPolicy",
  ];

  if (!docs) return true;

  return !requiredDocs.every((doc) => docs[doc] === true);
};

  const handleShortListClick = async (id, status) => {
    setIsFiltering(true);
    setCurrentFilter(status);

    try {
      await fetchPendingEmployeesByjobPostingId(id, status);
    } finally {
      setIsFiltering(false);
    }
  };

const handleFileDownload = (fileUrl, fileName, empId, type) => {
  if (!fileUrl) return;

  const link = document.createElement("a");
  link.href = fileUrl;
  link.setAttribute("download", fileName || "document");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setDownloadedDocs((prev) => ({
    ...prev,
    [empId]: {
      ...prev[empId],
      [type]: true,
    },
  }));
};

  function displayWarningMessage(emailData) {
    confirm({
      title: "Are you sure you want to send this email?",
      content: `You are about to send an email to this candidate.`,
      okText: "Yes, Send",
      okType: "primary",
      cancelText: "No, Cancel",
      onOk: async () => {
        await sendEmailToEmployee(emailData);
      },
    });
  }

  const sendEmailToEmployee = async (emailData) => {
    const { jobApplicantId } = emailData;

    setLoadingEmails((prev) => ({ ...prev, [jobApplicantId]: true }));

    try {
      await axiosInstance.post(API.hiringJobApplicant.sendEmail, emailData);
      toast.success("Successfully Sent");
      fetchEmployeesByjobPostingId(id);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email.");
    } finally {
      setLoadingEmails((prev) => ({ ...prev, [jobApplicantId]: false }));
    }
  };

  const handleSendEmail = (jobApplicantId) => {
    const payload = {
      jobApplicantId,
    };

    displayWarningMessage(payload);
  };

  // Brand-styled status action buttons. Removed standalone "View Feedback"
  // buttons everywhere — feedback is now accessible via the Feedback tab on
  // the Applicant Detail page (reached by "View Details").
  // Per Mudassir (May-20): when the job posting itself is Filled, all hiring
  // pipeline buttons in the candidate list are suppressed.
  const renderButton = (info) => {
    const isInterLoading = loadingInterviewEmails[info.email] || false;
    const jobOfferLoading = loadingJobOffer[info.email] || false;
    const isPostingFilled = jobDetails?.status === JobPostingStatusEnum.Filled;

    if (isPostingFilled) return null;

    switch (info.status) {
      case JobApplicationStatusEnum.Applied:
        return null;

      case JobApplicationStatusEnum.TermsAccepted:
        return (
          <button
            type="button"
            onClick={() =>
              showModal(
                info.jobApplicantId,
                info.email,
                info.firstDateTime,
                info.secondDateTime,
                info.thirdDateTime
              )
            }
            style={brandBtn(isInterLoading)}
            disabled={isInterLoading}
          >
            {isInterLoading ? "Please Wait..." : "📅 Interview"}
          </button>
        );

      // Per Mudassir: no Feedback button during InterviewInProgress —
      // feedback only appears once the interview has actually happened.
      case JobApplicationStatusEnum.InterviewInProgress:
        return null;

      // Per Mudassir (May-20 call): Post Feedback action belongs inside the
      // Applicant Detail page (Feedback tab), not on the list card.
      case JobApplicationStatusEnum.InterviewCompleted:
        return null;

      // Per Mudassir: View Feedback content lives in the Feedback tab inside
      // the detail page now; no outer "View Feedback" link from the list.
      case JobApplicationStatusEnum.FeedbackAdded:
        return null;

      // Per the latest review: the list card should only carry "View Details ->".
      // All status-specific actions (View Documents, Personal Info, etc.) live
      // inside the Applicant Detail page tabs — no duplicates on the list.
      case JobApplicationStatusEnum.DocumentSend:
      case JobApplicationStatusEnum.DocumentRecieved:
      case JobApplicationStatusEnum.OnboardingDone:
      case JobApplicationStatusEnum.Hired:
        return null;

      case JobApplicationStatusEnum.Reject:
        return (
          <button
            type="button"
            style={{
              padding: "7px 16px",
              background: "#fff0ee",
              color: "#7a1f1f",
              border: "1px solid #f5c2c2",
              borderRadius: "40px",
              fontSize: "0.8rem",
              fontWeight: 600,
              cursor: "not-allowed",
              fontFamily: "inherit",
            }}
            disabled
          >
            ✗ Rejected
          </button>
        );

      default:
        return null;
    }
  };

  async function fetchJobDetails() {
    try {
      const response = await axiosInstance.get(API.hiringJobPosting.getPostingById(id));
      setJobDetails(response.data);
    } catch (error) {
      console.error("Error fetching job details:", error);
    }
  }

  async function fetchEmployeesByjobPostingId(jobPostingId) {
    try {
      if (!jobPostingId || isNaN(Number(jobPostingId))) {
        console.warn("🚫 Blocked API call with invalid jobPostingId:", jobPostingId);
        return;
      }
      const response = await axiosInstance.get(
        API.hiringJobApplicant.getListByPosting(jobPostingId)
      );

      const responseData = response.data;
      const employeeJobApplications = response.data.applicants || [];

      //  build counts dynamically
      const counts = {};

      employeeJobApplications.forEach((app) => {
        const status = app.status;
        counts[status] = (counts[status] || 0) + 1;
      });

      setStatusCounts(counts);
      setEmployeeCount(employeeJobApplications.length);

      const updatedRows = employeeJobApplications.map((info) => ({
        key: info.jobApplicantId.toString(),
        jobApplicantId: info.jobApplicantId,
        source: info.source,
        firstName: info.firstName,
        source: info.source,
        lastName: info.lastName,
        email: info.email,
        mobileno: info.mobileNo,
        streetAddress: info.streetAddress,
        city: info.city,
        state: info.state,
        postalCode: info.postalCode,
        forJobApply: info.forJobApply,
        coverLetter: info.coverLetter,
        resume: info.resume,
        status: info.status,
        // AI fit score (nullable per Mudassir) — must be carried through the
        // map so the card pill can display it instead of N/A.
        fitScore: info.fitScore,
        dateAvailable: <span>{formatDateTime(info.dateAvailable)}</span>,
        appliedOn: <span>{formatDateTime(info.appliedOn)}</span>,
      }));

      setEmployeeDetails(updatedRows);
      setIsOverall(true);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  function renderStatus(status) {
    const statusLabel = JobApplicationStatusLabel[status];

    // Default green, but use red for Reject
    const color = status === JobApplicationStatusEnum.Reject ? "red" : "green";
    return <span style={{ color }}>{statusLabel}</span>;
  }

  function renderStatusText(status) {
    const statusText = JobApplicationStatusText[status];

    // Default green, but use red for Reject
    const color =
      status === JobApplicationStatusEnum.Reject ? "red" : "#3e709bff"; /* 60% opaque black */
    return <span style={{ color }}>{statusText}</span>;
  }

  const handleButtonClick = (id) => {
    setCurrentFilter(null);
    fetchEmployeesByjobPostingId(id);
  };

  const getFilterLabel = (status) => {
    return JobApplicationStatusLabel[status];
  };

  function formatDateTime(dateTimeString) {
    if (!dateTimeString) return "—";
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime()) || date.getFullYear() < 2000) return "—";
    const options = {
      year: "numeric",
      month: "long",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-US", options);
  }

  const handleShortlistClick = async (jobApplicantId) => {
    setLoadingEmails((prev) => ({ ...prev, [jobApplicantId]: true }));

    try {
      handleSendEmail(jobApplicantId);
    } finally {
      setLoadingEmails((prev) => ({ ...prev, [jobApplicantId]: false }));
    }
  };

  const handleDelete = (jobApplicantId) => {
    confirm({
      title: "Are you sure you want to reject this employee?",
      icon: <ExclamationCircleOutlined />,
      content: "After rejection, this action cannot be undone.",
      okText: "Yes, Reject",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          // Set the loading state to the current employee's ID
          setLoadingjobApplicantId(jobApplicantId);

        

          // Make the DELETE request with query parameters
          const response = await axiosInstance.delete(API.hiringJobApplicant.delete(jobApplicantId)
          );

          // Display the message from the backend in the toast
          toast.success(response.data);

          // Filter the employeeDetails to remove the rejected employee
          setEmployeeDetails((prevDetails) => {
            const updatedDetails = prevDetails.filter(
              (info) => info.jobApplicantId !== jobApplicantId
            );

            // If no employees left, set employeeDetails to null to show "No Candidate available"
            if (updatedDetails.length === 0) {
              return null;
            }

            return updatedDetails;
          });

          setEmployeeCount((prevCount) => prevCount - 1);
        } catch (error) {
          // Show the error message from the backend in the toast
          if (error.response && error.response.data) {
            toast.error(error.response.data);
          } else {
            toast.error("Failed to reject employee.");
          }
        } finally {
          // Reset the loading state after the request completes
          setLoadingjobApplicantId(null);
        }
      },
    });
  };

   //  View document in new tab
    const handlePdfPreview = (fileUrl) => {
      if (!fileUrl) return toast.warning("No file found");
  
      const cleanUrl = `${FILE_BASE_URL}/${fileUrl.replace(/^\/+/, "")}`;
  
      window.open(cleanUrl, "_blank");
    };

    const handleDownload = (fileUrl , fileName) => {
      if (!fileUrl) {
             toast.warning("No file to download");
             return;
    }

    try{
         const link = document.createElement("a");
         link.href = fileUrl;
         link.setAttribute("download", fileName || "document");
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         toast.success("Downloading started");

    } catch(error){
      console.error("Not Downlaoding", error);
    }
  }

  const handlecoverLetterPdfPreview = (pdfUrl) => setSelectedCoverLetter(pdfUrl);
  async function fetchPendingEmployeesByjobPostingId(jobPostingId, status) {
    try {
      const response = await axiosInstance.get(
        API.hiringJobApplicant.getListByPosting(jobPostingId)
      );
      const responseData = response.data;
      const employeeJobApplications = responseData.applicants || [];
      const filteredEmployees = employeeJobApplications.filter((info) => info.status === status);

      const updatedRows = filteredEmployees.map((info) => ({
        key: info.jobApplicantId.toString(),
        jobApplicantId: info.jobApplicantId,
        firstName: info.firstName,
        lastName: info.lastName,
        email: info.email,
        mobileno: info.mobileNo,
        streetAddress: info.streetAddress,
        city: info.city,
        state: info.state,
        postalCode: info.postalCode,
        forJobApply: info.forJobApply,
        coverLetter: info.coverLetter,
        resume: info.resume,
        status: info.status,
        password: info.password,
        // Carry the AI fit score so the card pill can show it on the
        // filtered (status-button) view too.
        fitScore: info.fitScore,
        dateAvailable: <span>{formatDateTime(info.dateAvailable)}</span>,
        appliedOn: <span>{formatDateTime(info.appliedOn)}</span>,
      }));

      // Set the filtered data
      setEmployeeDetails(updatedRows);
      setIsOverall(false);

      // Add this to scroll to top after filtering
      setTimeout(() => {
        const container = document.querySelector(".scrollable-container");
        if (container) {
          container.scrollTop = 0;
        }
      }, 100);

      return updatedRows;
    } catch (error) {
      console.error("Error fetching filtered data:", error);

      return [];
    }
  }

  const handleOnboarding = (empId, status) => {
    // Ensure empId has a value
    if (!empId) {
      toast.error("Critical: Employee ID is missing!");
      return;
    }

    // Navigate based on status
    if (status === JobApplicationStatusEnum.OnboardingDone) {
      // Redirect to humanResource page if onboarding is done
      navigate(`/humanResource/${empId}`);
    } else {
      // Normal onboarding flow
      navigate(`/onboarding`);
    }
  };

   const columns = [
  {
    title: "Document Name",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "Actions",
    key: "actions",
   render: (_, record) => (
  <Space>
    <Button
      type="link"
      onClick={() => handlePdfPreview(record.file)}
    >
      View
    </Button>

    <Button
      type="link"
      icon={<DownloadOutlined />}
      onClick={() =>
        handleFileDownload(
          record.file,
          record.name,
          employeeContract?.jobApplicant?.jobApplicantId, // 🔥 FIX HERE
          record.key
        )
      }
    >
      Download
    </Button>
  </Space>
)
  },
];

  return {
    // core render helpers
    renderButton,
    renderStatus,
    renderStatusText,

    // data
    jobDetails,
    employeeDetails,
    employeeCount,
    statusCounts,

    // filtering
    handleShortListClick,
    currentFilter,
    isFiltering,
    getFilterLabel,

    // actions
    handleDelete,
    handlePdfPreview,
    handlecoverLetterPdfPreview,
    handleOnboarding,

    // modals & interview
    open,
    setOpen,
    jobApplicantId,
    email,
    firstDateTime,
    secondDateTime,
    thirdDateTime,
    setFirstDateTime,
    setSecondDateTime,
    setThirdDateTime,
    handleOk,
    handleCancel,

    // contract
    openContract,
    employeeContract,
    contractPdf,
    handleContractCancel,
    employeeEmail,
    empId,

    // misc
    columns,
    selectedPdf,
    selectedCoverLetter,
    loading,
    loadingjobApplicantId,
    formatDateTime,
    jobPostingId,
    // feedback
    feedbacks,
    feedbackData,
    setFeedbackData,
    handleSubmitFeedback,
    openfeedback,
    setOpenfeedback,
    handleFileDownload,
    isOnboardingDisabled
  };
}

export default ViewCandidateService;
